import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Calendar, ExternalLink, Users } from 'lucide-react';
import { useCopyLink } from '@/hooks/use-copy-link';
import { CampaignTypeBadge } from '@/components/shared/campaign-type-badge';
import { CopyButton } from '@/components/shared/copy-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatDate } from '@/utils/date';
import { useGetCampaign } from '@/api/campaigns/get-campaign';
import { generateCampaignShareLink } from '@/utils/campaign-share-link.ts';
import { useOrganization } from '@/contexts/organization-context';

export const Route = createFileRoute('/_authenticated/campaigns/$campaignId')({
  component: CampaignDetailPage
});

function CampaignDetailPage() {
  const navigate = useNavigate();
  const { campaignId } = Route.useParams();
  const { organizationId, projectId } = useOrganization();
  const { copyLink, isLinkCopied } = useCopyLink();
  const campaignQuery = useGetCampaign(organizationId, projectId, campaignId);

  if (!projectId || !organizationId) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-4xl space-y-6 text-center">
            <h1 className="text-2xl font-bold">Projet et organisation requis</h1>
            <p className="text-muted-foreground">
              Impossible d'afficher la campagne sans projet ou organisation sélectionné.
            </p>
            <Button variant="hero" onClick={() => navigate({ to: '/onboarding' })}>
              Retour à l'onboarding
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (campaignQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-6xl space-y-6 text-muted-foreground">Chargement...</div>
        </div>
      </div>
    );
  }

  if (campaignQuery.isError || !campaignQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-20 px-4">
          <div className="container mx-auto max-w-4xl space-y-6 text-center">
            <h1 className="text-2xl font-bold">Campagne introuvable</h1>
            <p className="text-muted-foreground">Cette campagne n'existe pas ou vous n'avez pas accès.</p>
            <Button variant="hero" onClick={() => navigate({ to: '/campaigns' })}>
              Retour aux campagnes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const campaign = campaignQuery.data;
  const shareLink = generateCampaignShareLink(campaign.id, campaign.campaignType);

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-6xl space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Link to="/campaigns">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold">{campaign.name}</h1>
                <CampaignTypeBadge type={campaign.campaignType} />
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Créée le {formatDate(campaign.createdOn)}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {campaign.feedbacks.length} feedback{campaign.feedbacks.length > 1 ? 's' : ''} reçu
                  {campaign.feedbacks.length > 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Informations de la campagne */}
          <Card>
            <CardHeader>
              <CardTitle>Lien de partage</CardTitle>
              <CardDescription>Partagez ce lien avec vos utilisateurs pour collecter leurs feedbacks.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <Input
                  value={shareLink}
                  readOnly
                  className="flex-1 font-mono text-sm border-none bg-transparent px-0"
                />
                <CopyButton isCopied={isLinkCopied()} onCopy={() => copyLink(shareLink)} className="shrink-0" />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => window.open(shareLink, '_blank')}
                  className="shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Feedbacks */}
          <Card>
            <CardHeader>
              <CardTitle>Feedbacks</CardTitle>
              <CardDescription>Liste des feedbacks reçus pour cette campagne.</CardDescription>
            </CardHeader>
            <CardContent>
              {campaign.feedbacks.length === 0 ? (
                <div className="text-center py-12">
                  <div className="p-4 bg-muted rounded-full w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Aucun feedback reçu</h3>
                  <p className="text-muted-foreground mb-4">
                    Partagez le lien de la campagne pour commencer à recevoir des feedbacks.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaign.feedbacks.map((feedback) => (
                    <Card key={feedback.id} className="border-l-4 border-l-primary">
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          {feedback.title && <h4 className="font-semibold">{feedback.title}</h4>}
                          <p className="text-sm text-muted-foreground">{feedback.content}</p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(feedback.createdOn, true)}
                            </div>
                            <div className="text-muted-foreground">{feedback.createdBy}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
